import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { KeyboardAvoidingView, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStore } from 'zustand';

import { ProvideReferenceList } from './ProvideReferenceList';
import { SearchingStatementList } from './SearchingStatementList';
import {
  CitationWizardSteps,
  type HeaderPublishCta,
  InlineReviewPhase,
  pickPhaseTitle,
} from './statementTriageWizard';
import { useProvidePublishedFeedback } from './useProvidePublishedFeedback';
import { useProvideReferencePublish } from '../../hooks/useProvideReferencePublish';
import { useStatementTriageSheet } from '../../hooks/useStatementTriageSheet';
import type { PublishProvidedStatementReferencesResult } from '../../model/providedReferences';
import {
  citationWizardHandoffStore,
  clearCitationWizardHandoff,
  type CitationWizardHandoffPayload,
} from '../../state/citationWizardHandoffStore';
import { deleteProvideReferenceDraftStore } from '../../state/provideReferenceDraftStore';
import { getStatementTriageStore } from '../../state/statementTriageStore';

import { headerItem } from '@/components/HeaderActions';
import { MobileNumberSetupForm } from '@/components/MobileNumberSetupForm';
import { Header } from '@/components/header';
import type { HeaderItem } from '@/components/headerItems/types';
import { Box } from '@/components/ui/box';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { ScrollView } from '@/components/ui/scroll-view';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { PhaseEnter } from '@/components/wizard/PhaseEnter';
import { MOTION } from '@/constants/motion';
import { SMS_NOTIFICATION_COPY } from '@/constants/smsPromptCopy';
import { useSmsNotifySetup } from '@/hooks/useSmsNotifySetup';
import { useToaster } from '@/hooks/useToaster';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { trackReferenceWizardNotifyRequested } from '@/util/analyticsEvents';

/**
 * Prototype: full-screen modal route variant of the statement-citation
 * triage wizard for native (iOS + Android). Replaces the inline
 * Actionsheet surface — see `app/(secure)/_layout.tsx` for the
 * `presentation: 'fullScreenModal'` registration and
 * `AddReferencesPromptBubble.tsx` for the trigger.
 *
 * Web continues to use the existing `StatementTriageSheet.web.tsx`
 * Modal mounted inline in the bubble; this route is native-only.
 *
 * Data plumbing: the trigger writes the heavy inputs (extracted
 * statements, statuses, attached refs, help requests) into
 * `citationWizardHandoffStore` synchronously before `router.push`. The
 * route reads the payload on mount; the hook itself subscribes to the
 * live citation request, which is the only source that needs to stay
 * live during the wizard.
 */
export function CitationWizardScreen() {
  const router = useRouter();

  const { expectedMessageSid } = useLocalSearchParams<{ expectedMessageSid?: string }>();

  const handoff = useStore(citationWizardHandoffStore, (state) => state.payload);

  useEffect(() => {
    return () => {
      clearCitationWizardHandoff();
    };
  }, []);

  if (!handoff || (expectedMessageSid && handoff.messageSid !== expectedMessageSid)) {
    return <MissingHandoffGuard />;
  }

  return <WizardBody handoff={handoff} onDismiss={() => router.dismiss()} />;
}

function MissingHandoffGuard() {
  const router = useRouter();

  useEffect(() => {
    if (router.canDismiss()) {
      router.dismiss();
    }
  }, [router]);

  return null;
}

interface WizardBodyProps {
  handoff: CitationWizardHandoffPayload;
  onDismiss: () => void;
}

function WizardBody({ handoff, onDismiss }: WizardBodyProps) {
  /**
   * Read insets via this hook, not a SafeAreaView wrapper. Inside a full-screen
   * modal the native safe-area view measures stale insets.
   */
  const { bottom } = useSafeAreaInsets();

  const {
    conversationId,
    messageSid,
    extractedStatements,
    statementCitationStatuses,
    attachedStatementReferences,
    statementReferenceHelpRequests,
    entryMode: handoffEntryMode,
    openSource: handoffOpenSource,
  } = handoff;

  const statementCount = extractedStatements.length;

  const {
    phase,
    entryMode,
    provideStatementIndices,
    activeCitationRequest,
    isSaving,
    errorMessage,
    isAutoStartPending,
    handleAutoStart,
    handleDismiss,
  } = useStatementTriageSheet({
    conversationId,
    messageSid,
    statementCount,
    statementCitationStatuses,
    currentAttachedStatementReferences: attachedStatementReferences,
  });

  const confirmProvidePublished = useProvidePublishedFeedback();

  const handleProvidePublished = useCallback(
    (result: PublishProvidedStatementReferencesResult) => {
      confirmProvidePublished(result);

      onDismiss();
    },
    [confirmProvidePublished, onDismiss]
  );

  const {
    canPublish: canPublishProvide,
    isPublishing: isProvidePublishing,
    errorMessage: provideErrorMessage,
    handlePublish: handleProvidePublish,
  } = useProvideReferencePublish({
    conversationId,
    messageSid,
    statementIndices: provideStatementIndices,
    currentReferences: attachedStatementReferences,
    currentHelpRequests: statementReferenceHelpRequests,
    currentStatementCitationStatuses: statementCitationStatuses,
    onPublished: handleProvidePublished,
  });

  // The route IS the open state on native. Flip `isOpen` to true on
  // mount so the hook's OPENED-analytics transition effect fires
  // exactly once per route entry. Flip back to false on unmount so
  // the next push starts cleanly.
  useEffect(() => {
    const store = getStatementTriageStore(messageSid);

    // Apply the entry mode from the handoff before open() so the wizard
    // reflects the chosen flow on first paint. Each entry resets first so
    // a stale session left in the per-message store (the expert opened
    // the other flow last time and dismissed) never bleeds across.
    if ((handoffEntryMode ?? 'search') === 'provide') {
      store.getState().reset();

      store.getState().setEntryMode('provide');

      // Provide is a single step: jump straight to the provide phase with
      // a row per statement (each opt-in), skipping selection. Indices are
      // derived from the count (the rows are always 0..n-1) so the effect
      // depends on the primitive `statementCount`, not the freshly-spread
      // `extractedStatements` array reference — which changes on every
      // handoff push and would otherwise re-fire this effect and wipe
      // in-progress drafts.
      store
        .getState()
        .enterProvide(Array.from({ length: statementCount }, (_value, index) => index));
    } else {
      if (store.getState().entryMode === 'provide') {
        store.getState().reset();
      }

      store.getState().setEntryMode('search');
    }

    store.getState().setOpenSource(handoffOpenSource ?? 'inline_prompt');

    store.getState().open();

    return () => {
      if (store.getState().isOpen) {
        store.getState().close();
      }
    };
  }, [messageSid, handoffEntryMode, handoffOpenSource, statementCount]);

  // Evict the per-message provide draft store when the route unmounts so
  // dismissed/published sessions don't linger in the registry.
  useEffect(
    () => () => {
      deleteProvideReferenceDraftStore(messageSid);
    },
    [messageSid]
  );

  const onCancelPress = useCallback(() => {
    if (isSaving || isProvidePublishing) {
      return;
    }

    handleDismiss();

    onDismiss();
  }, [handleDismiss, isSaving, isProvidePublishing, onDismiss]);

  const toast = useToaster();

  const { smsWillReach, hasPhoneNumber, mobilePhoneSliced, referencesReadySmsEnabled } =
    useSmsNotifySetup();

  /**
   * When true, the phone-setup form replaces the phase body as a scrollable,
   * keyboard-aware step. It can't be a bottom sheet / overlay here: this is a
   * `fullScreenModal` route, which is presented ABOVE the root providers, so
   * any overlay (such as a Gluestack Actionsheet) renders behind it.
   * Replacing the body (rather than appending) keeps it from overflowing the
   * viewport or colliding with the review surface; the stepper above stays
   * visible as the progress cue.
   */
  const [showPhoneSetup, setShowPhoneSetup] = useState(false);

  /**
   * Dismiss the wizard route and confirm the expert will be notified when
   * the search completes (the `referencesReady` deep link reopens review).
   */
  const finishNotify = useCallback(() => {
    handleDismiss();

    onDismiss();

    toast.showSuccess({
      title: 'On it',
      message: "We'll let you know when your references are ready.",
    });
  }, [handleDismiss, onDismiss, toast]);

  /**
   * Searching-phase "Notify me when ready". Open the phone-setup sheet when
   * SMS won't reach the expert (no number, or `referencesReady` SMS off) so
   * they stay on the wizard with the search visible behind it; otherwise SMS
   * already reaches them, so dismiss the wizard and confirm.
   */
  const handleNotifyWhenReady = useCallback(() => {
    trackReferenceWizardNotifyRequested({
      conversationId,
      messageSid,
      hasPhoneNumber,
      referencesReadySmsEnabled,
    });

    if (!smsWillReach) {
      setShowPhoneSetup(true);

      return;
    }

    finishNotify();
  }, [
    conversationId,
    messageSid,
    finishNotify,
    hasPhoneNumber,
    referencesReadySmsEnabled,
    smsWillReach,
  ]);

  /**
   * Resolve the phone prompt (Save or Not now). The expert tapped "Notify me
   * when ready" — intent is to leave and be notified — so if results landed
   * while they were entering their number, reveal the review; otherwise the
   * search is still running, so dismiss the wizard and confirm.
   */
  const resolvePhoneSetup = useCallback(() => {
    setShowPhoneSetup(false);

    if (phase === 'review' && activeCitationRequest) {
      return;
    }

    finishNotify();
  }, [phase, activeCitationRequest, finishNotify]);

  /**
   * Publish CTA mirrored up from `InlineReviewPhase` so we can render
   * it in the header right slot. `null` while the review phase isn't
   * mounted, has no candidates, or is unmounting — the header just
   * renders a layout-only spacer in that case so the title stays
   * centered.
   */
  const [headerPublishCta, setHeaderPublishCta] = useState<HeaderPublishCta | null>(null);

  const title = pickPhaseTitle(phase);

  /**
   * Publish CTA for the header right slot. The review phase mirrors its
   * CTA up via `headerPublishCta`; the provide phase has no inline review
   * surface, so its Publish action is built here so the header slot
   * carries the primary action on `provide` too — matching review and
   * keeping the wizard's commit button in one consistent place.
   */
  const headerCta: HeaderPublishCta | null =
    phase === 'provide'
      ? {
          label: 'Publish',
          onPress: handleProvidePublish,
          isPublishing: isProvidePublishing,
          isDisabled: !canPublishProvide,
        }
      : headerPublishCta;

  /**
   * Publish is always in the bar and hidden until the review phase offers it, so the bar keeps
   * the same items across the wizard's steps.
   */
  const publishItem: HeaderItem = {
    type: 'button',
    key: 'publish',
    label: headerCta?.label ?? 'Publish',
    role: 'confirm',
    isHidden: !headerCta,
    isDisabled: headerCta?.isDisabled,
    isLoading: headerCta?.isPublishing,
    onPress: () => headerCta?.onPress(),
  };

  return (
    <Box
      testID="citation-wizard-screen"
      className="flex-1 bg-background-0"
      style={{ paddingBottom: bottom }}>
      <Header
        headerShown
        coversActingAsBanner
        title={title}
        leftItems={[
          { ...headerItem.cancel(onCancelPress), isDisabled: isSaving || isProvidePublishing },
        ]}
        rightItems={[publishItem]}
      />

      <VStack className="w-full flex-1">
        {entryMode === 'search' ? <CitationWizardSteps phase={phase} /> : null}

        <Box className="flex-1">
          {/* Phone-setup replaces the phase body (rather than appending or
                  overlaying) so it can't overflow the viewport or collide with
                  the review surface. Scrollable + keyboard-aware so the field
                  and buttons stay reachable when the keyboard is up. */}
          {showPhoneSetup ? (
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'padding' : undefined}
              style={{ flex: 1 }}>
              <ScrollView className="flex-1" keyboardShouldPersistTaps="handled">
                {/* Fade-up entrance when the prompt mounts (it only mounts when
                    `showPhoneSetup` flips), so it eases in rather than swapping
                    in abruptly. Wraps the content, not the flex ScrollView, to
                    avoid PhaseEnter's flex-sizing quirk. */}
                <PhaseEnter preset={MOTION.entrance.standard}>
                  <VStack className="w-full gap-3 px-4 pb-8 pt-2" accessibilityLiveRegion="polite">
                    <Text size="lg" className="font-semibold text-typography-900">
                      {SMS_NOTIFICATION_COPY.reference_wizard.heading}
                    </Text>
                    <Text size="sm" className="text-typography-700">
                      {hasPhoneNumber
                        ? "We'll text you the moment your references are ready to review."
                        : "Add your mobile number and we'll text you the moment your references are ready to review."}
                    </Text>
                    <MobileNumberSetupForm
                      source="reference_wizard"
                      showHeader={false}
                      autoFocus
                      enableSmsOnSave
                      showSuccessToast={false}
                      defaultMobilePhone={mobilePhoneSliced}
                      requireDirty={!hasPhoneNumber}
                      onComplete={resolvePhoneSetup}
                      onSkip={resolvePhoneSetup}
                    />
                  </VStack>
                </PhaseEnter>
              </ScrollView>
            </KeyboardAvoidingView>
          ) : null}

          {/* The statement picker has been removed: opening the search
              wizard fires the search for all statements automatically. A
              brief spinner covers the pre-fire frame and the in-flight
              create POST; if the create fails, the selection phase shows
              the error with a retry. One persistent live-region container
              holds both states so the spinner → error swap is announced. */}
          {!showPhoneSetup && phase === 'selection' && (isAutoStartPending || errorMessage) ? (
            <VStack
              className="flex-1 items-center justify-center gap-3 px-4"
              accessibilityLiveRegion="polite">
              {isAutoStartPending ? (
                <>
                  <Spinner
                    size="large"
                    aria-hidden
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                  />
                  <Text size="sm" className="text-typography-600">
                    Starting reference search…
                  </Text>
                </>
              ) : (
                <>
                  <Text size="sm" className="text-center text-error-600">
                    {errorMessage}
                  </Text>
                  <Button
                    onPress={handleAutoStart}
                    action="primary"
                    variant="solid"
                    size="md"
                    isDisabled={isSaving}
                    hitSlop={getAccessibleHitSlop(40)}
                    aria-label="Try the reference search again">
                    <ButtonText>Try again</ButtonText>
                  </Button>
                </>
              )}
            </VStack>
          ) : null}

          {!showPhoneSetup && phase === 'searching' ? (
            <PhaseEnter preset={MOTION.entrance.standard}>
              <SearchingStatementList
                messageSid={messageSid}
                extractedStatements={extractedStatements}
              />
            </PhaseEnter>
          ) : null}

          {!showPhoneSetup && phase === 'provide' ? (
            <PhaseEnter preset={MOTION.entrance.standard}>
              <ProvideReferenceList
                messageSid={messageSid}
                extractedStatements={extractedStatements}
                statementIndices={provideStatementIndices}
                isPublishing={isProvidePublishing}
                onSubmit={handleProvidePublish}
              />
              {provideErrorMessage ? (
                <Text
                  size="sm"
                  className="px-4 pb-1 text-error-600"
                  accessibilityLiveRegion="polite">
                  {provideErrorMessage}
                </Text>
              ) : null}
            </PhaseEnter>
          ) : null}

          {!showPhoneSetup && phase === 'review' && activeCitationRequest ? (
            // PhaseEnter (Animated.View with opacity-only style) doesn't
            // propagate flex sizing reliably for the review surface's
            // column layout (body flex-1, footer pinned). Mount the
            // phase body directly so its inner `VStack flex-1` fills
            // the parent `Box flex-1` cleanly. The entrance animation
            // is skipped on review — acceptable for the prototype.
            <InlineReviewPhase
              conversationId={conversationId}
              citationRequest={activeCitationRequest}
              extractedStatements={extractedStatements}
              attachedStatementReferences={attachedStatementReferences}
              statementReferenceHelpRequests={statementReferenceHelpRequests}
              onPublishSuccess={onDismiss}
              onPublishCtaChange={setHeaderPublishCta}
              hideStickyFooter
            />
          ) : null}
        </Box>

        {/* Phase-specific footer pinned to bottom with safe-area
            padding. Selection has no footer (the picker is gone — the
            search auto-starts); review uses its own footer from
            InlineReviewPhase. */}
        {!showPhoneSetup && phase === 'searching' ? (
          <HStack className="w-full items-center justify-end border-t border-outline-100 px-4 pb-3 pt-3">
            <Button
              onPress={handleNotifyWhenReady}
              action="secondary"
              variant="outline"
              size="md"
              aria-label="Notify me when ready">
              <ButtonText>Notify me when ready</ButtonText>
            </Button>
          </HStack>
        ) : null}
      </VStack>
    </Box>
  );
}
